import { Card } from "primereact/card";
import { SafeReportResponse } from "../Conversion";
import { DataTable, DataTableRowReorderEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { useState } from "react";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import { useReorderPolicyField, useUpdateReport } from "../data";
import { keysForDrop } from "./sortKeys";
import styles from "./PolicyFieldsCard.module.css";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { TabView, TabPanel } from "primereact/tabview";
import * as client from "../../../client";
import { readableNameFromPeril } from "../general-input/util";
import { formatMonetaryValue } from "./util";
import {
  applyDeductibleAdd,
  applyDeductibleDelete,
  applyDeductibleEdit,
  applyLimitAdd,
  applyLimitDelete,
  applyLimitEdit,
  applySublimitDelete,
  applySublimitEdit,
} from "./policyEdits";
import { effectiveExcerpts } from "./excerpts";
import { getPolicySourceNames } from "../utils";
import { Link } from "react-router-dom";
import { ROUTES } from "../../../routes";

const PERIL_TYPES = Object.values(client.peril_type) as client.peril_type[];

const PERIL_OPTIONS: { label: string; value: client.peril_type }[] =
  PERIL_TYPES.map((p) => ({ label: readableNameFromPeril(p), value: p }));

// Canonical peril display order, mirroring the backend PerilType order, so the
// table groups perils the same way PolicyFly emits them (see ordered_perils).
const PERIL_RANK = new Map<string, number>(PERIL_TYPES.map((p, i) => [p, i]));

/** Modal shell with a Cancel/Confirm footer. Calls onClose (which should reset
 *  the caller's form state) on cancel, dismiss, or a successful confirm. */
const FormDialog: React.FC<{
  visible: boolean;
  header: string;
  canConfirm: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ visible, header, canConfirm, onConfirm, onClose, children }) => (
  <Dialog
    visible={visible}
    header={header}
    style={{ width: "600px" }}
    onHide={onClose}
    footer={
      <div>
        <Button label="Cancel" text severity="secondary" onClick={onClose} />
        <Button
          label="Confirm"
          disabled={!canConfirm}
          onClick={() => {
            if (canConfirm) {
              onConfirm();
              onClose();
            }
          }}
        />
      </div>
    }
  >
    <div className={styles.verticalContainer}>{children}</div>
  </Dialog>
);

/** Small dialog component for adding a manual limit or deductible with PrimeReact controls. */
const AddManualEntryDialog: React.FC<{
  visible: boolean;
  header: string;
  namePlaceholder: string;
  descriptionPlaceholder: string;
  isDuplicateName: (peril: client.peril_type, name: string) => boolean;
  onHide: () => void;
  onConfirm: (
    peril: client.peril_type,
    name: string,
    description: string
  ) => void;
}> = ({
  visible,
  header,
  namePlaceholder,
  descriptionPlaceholder,
  isDuplicateName,
  onHide,
  onConfirm,
}) => {
  const [peril, setPeril] = useState<client.peril_type | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const trimmedName = name.trim();
  // Block a name that collides case-insensitively with an existing entry in the
  // peril: adds join by casefolded name, so it would silently overwrite that
  // entry's value. The user edits the existing entry to change its value.
  const duplicate =
    peril != null && trimmedName !== "" && isDuplicateName(peril, trimmedName);

  const close = () => {
    setPeril(null);
    setName("");
    setDescription("");
    onHide();
  };

  return (
    <FormDialog
      visible={visible}
      header={header}
      canConfirm={peril != null && trimmedName !== "" && !duplicate}
      onConfirm={() => {
        if (peril) onConfirm(peril, trimmedName, description);
      }}
      onClose={close}
    >
      <Dropdown
        options={PERIL_OPTIONS}
        optionLabel="label"
        optionValue="value"
        placeholder="Select peril type"
        className={styles.confirmDialogWidth}
        value={peril}
        onChange={(e) => setPeril(e.value)}
      />
      <InputText
        className={styles.confirmDialogWidth}
        placeholder={namePlaceholder}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <InputTextarea
        className={styles.confirmDialogWidth}
        placeholder={descriptionPlaceholder}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        autoResize
        rows={3}
      />
      {duplicate && (
        <Message
          severity="warn"
          text={`A "${trimmedName}" entry already exists for this peril (names match ignoring case). Edit that entry to change its value.`}
        />
      )}
    </FormDialog>
  );
};

/** Controlled dialog for adding a manual sublimit. The category selects which
 *  classification block the row lands in ("Default" => null classification).
 *  Blocks confirming when the trimmed name is empty or duplicates an existing
 *  sublimit in the chosen category (which would otherwise collide on the table
 *  row key). */
const AddSublimitDialog: React.FC<{
  visible: boolean;
  isDuplicateName: (
    name: string,
    classification: client.Sublimit["classification"]
  ) => boolean;
  onHide: () => void;
  onConfirm: (
    name: string,
    value: string,
    classification: client.Sublimit["classification"]
  ) => void;
}> = ({ visible, isDuplicateName, onHide, onConfirm }) => {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [category, setCategory] = useState<SublimitCategory>("default");

  const classification = classificationFromCategory(category);
  const trimmedName = name.trim();
  const duplicate =
    trimmedName !== "" && isDuplicateName(trimmedName, classification);

  const close = () => {
    setName("");
    setValue("");
    setCategory("default");
    onHide();
  };

  return (
    <FormDialog
      visible={visible}
      header="Add Sublimit"
      canConfirm={trimmedName !== "" && !duplicate}
      onConfirm={() => onConfirm(trimmedName, value, classification)}
      onClose={close}
    >
      <Dropdown
        className={styles.confirmDialogWidth}
        value={category}
        options={SUBLIMIT_CATEGORY_OPTIONS}
        onChange={(e) => setCategory(e.value)}
      />
      <InputText
        className={styles.confirmDialogWidth}
        placeholder="Sublimit name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <InputTextarea
        className={styles.confirmDialogWidth}
        placeholder="Sublimit value"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoResize
        rows={3}
      />
      {duplicate && (
        <Message
          severity="warn"
          text={`A sublimit named "${trimmedName}" already exists in this classification.`}
        />
      )}
    </FormDialog>
  );
};

interface PolicyFieldsCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

type DeductibleRow = {
  perilType: client.peril_type;
  readablePerilName: string;
  deductibleName: string;
  value: string;
  manual: boolean;
  sortKey: string | null;
};

type LimitRow = {
  perilType: client.peril_type;
  readablePerilName: string;
  limitName: string;
  value: string;
  manual: boolean;
  sortKey: string | null;
};

// Order excerpt rows: peril groups in canonical PerilType order (PERIL_RANK,
// matching the backend so the UI and PolicyFly group identically), then rows
// within a peril by their fractional key (unkeyed rows last).
const byPerilThenSortKey = (
  a: { perilType: client.peril_type; sortKey: string | null },
  b: { perilType: client.peril_type; sortKey: string | null }
): number => {
  const byPeril =
    (PERIL_RANK.get(a.perilType) ?? Number.MAX_SAFE_INTEGER) -
    (PERIL_RANK.get(b.perilType) ?? Number.MAX_SAFE_INTEGER);
  if (byPeril !== 0) return byPeril;
  // Keep any unranked perils grouped (don't interleave by sort_key across perils).
  if (a.perilType !== b.perilType) return a.perilType < b.perilType ? -1 : 1;
  if (a.sortKey === null && b.sortKey === null) return 0;
  if (a.sortKey === null) return 1;
  if (b.sortKey === null) return -1;
  return a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0;
};

const classificationOrder: Record<string, number> = {
  "": 0,
  time_limit: 1,
  distance_limit: 2,
  waiting_period: 3,
};

const readableClassification = (c: string | null | undefined): string => {
  switch (c) {
    case "time_limit":
      return "Time Limits";
    case "distance_limit":
      return "Distance Limits";
    case "waiting_period":
      return "Waiting Periods";
    default:
      return "Default";
  }
};

// A manual sublimit's category. "default" is the uncategorized bucket the
// backend represents as a null classification; the other values map straight
// through to their SublimitClassification.
type SublimitCategory =
  | NonNullable<client.Sublimit["classification"]>
  | "default";

const SUBLIMIT_CATEGORY_OPTIONS: { label: string; value: SublimitCategory }[] =
  [
    { label: "Default", value: "default" },
    { label: "Time Limit", value: "time_limit" },
    { label: "Distance Limit", value: "distance_limit" },
    { label: "Waiting Period", value: "waiting_period" },
  ];

const classificationFromCategory = (
  category: SublimitCategory
): client.Sublimit["classification"] =>
  category === "default" ? null : category;

/** Build flattened deductible rows from an AdvancedDeductibleInfo for a given policy.
 *  Manual entries take precedence: if a peril exists in manualDeductibles, extracted entries for that peril are skipped.
 *  Rows whose excerpt name is in removed_excerpt_names are omitted. */
const buildDeductibleRows = (
  extractedForPolicy: Record<string, client.AdvancedDeductible>,
  manualDeductibles: Record<string, client.AdvancedDeductible>
): DeductibleRow[] => {
  const rows: DeductibleRow[] = [];
  const manualPerilKeys = new Set(Object.keys(manualDeductibles));
  const addRows = (
    perilKey: string,
    ded: client.AdvancedDeductible,
    manualPeril: boolean
  ): void => {
    for (const excerpt of effectiveExcerpts(ded)) {
      rows.push({
        perilType: perilKey as client.peril_type,
        readablePerilName: readableNameFromPeril(perilKey as client.peril_type),
        deductibleName: excerpt.name,
        value: excerpt.value,
        manual: manualPeril || excerpt.isOverride,
        sortKey: excerpt.sortKey,
      });
    }
  };
  for (const [perilKey, ded] of Object.entries(extractedForPolicy)) {
    if (manualPerilKeys.has(perilKey)) continue;
    addRows(perilKey, ded, false);
  }
  for (const [perilKey, ded] of Object.entries(manualDeductibles)) {
    addRows(perilKey, ded, true);
  }
  return rows.sort(byPerilThenSortKey);
};

/** Build flattened limit rows from an AdvancedLimitInfo for a given policy.
 *  Manual entries take precedence: if a peril exists in manualLimits, extracted entries for that peril are skipped.
 *  Rows whose excerpt name is in removed_excerpt_names are omitted. */
const buildLimitRows = (
  extractedForPolicy: Record<string, client.AdvancedLimit>,
  manualLimits: Record<string, client.AdvancedLimit>
): LimitRow[] => {
  const rows: LimitRow[] = [];
  const manualPerilKeys = new Set(Object.keys(manualLimits));
  const addRows = (
    perilKey: string,
    lim: client.AdvancedLimit,
    manualPeril: boolean
  ): void => {
    for (const excerpt of effectiveExcerpts(lim)) {
      rows.push({
        perilType: perilKey as client.peril_type,
        readablePerilName: readableNameFromPeril(perilKey as client.peril_type),
        limitName: excerpt.name,
        value: excerpt.value,
        manual: manualPeril || excerpt.isOverride,
        sortKey: excerpt.sortKey,
      });
    }
  };
  for (const [perilKey, lim] of Object.entries(extractedForPolicy)) {
    if (manualPerilKeys.has(perilKey)) continue;
    addRows(perilKey, lim, false);
  }
  for (const [perilKey, lim] of Object.entries(manualLimits)) {
    addRows(perilKey, lim, true);
  }
  return rows.sort(byPerilThenSortKey);
};

export const PolicyFieldsCard: React.FC<PolicyFieldsCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);
  const reorderMutation = useReorderPolicyField(
    specialtyPropertyInfo.report_id
  );
  // Disable actions (and dragging) while any save is in flight — including a
  // reorder, whose optimistic move has already landed but is still persisting.
  const isMutating = updateReport.isPending || reorderMutation.isPending;

  // --- Sublimit source data (not cloned, sublimits save immediately) ---
  const manualSublimits =
    specialtyPropertyInfo.report_json.sublimit_info?.manual_sublimits ?? [];
  const extractedSublimitInfo =
    specialtyPropertyInfo.report_json.sublimit_info?.extracted_sublimits ?? {};

  const deductibleInfo = specialtyPropertyInfo.report_json.deductible_info;
  const limitInfo = specialtyPropertyInfo.report_json.limit_info;

  // --- Policy selector ---
  const policyNames = getPolicySourceNames(
    specialtyPropertyInfo.report_json.sublimit_info,
    deductibleInfo,
    limitInfo
  );
  const allPolicyValues = policyNames.map((name) => ({
    code: name,
    label: name,
  }));
  const [selectedExtractedPolicy, setSelectedExtractedPolicy] = useState(
    specialtyPropertyInfo.report_json.policy_info?.selected_policy_form_name &&
      policyNames.includes(
        specialtyPropertyInfo.report_json.policy_info?.selected_policy_form_name
      )
      ? {
          code: specialtyPropertyInfo.report_json.policy_info
            ?.selected_policy_form_name,
          label:
            specialtyPropertyInfo.report_json.policy_info
              ?.selected_policy_form_name,
        }
      : undefined
  );

  const savedPolicyFormName =
    specialtyPropertyInfo.report_json.policy_info?.selected_policy_form_name;
  const policyFormMismatch =
    selectedExtractedPolicy?.code != null &&
    savedPolicyFormName != null &&
    selectedExtractedPolicy.code !== savedPolicyFormName;

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [addLimitVisible, setAddLimitVisible] = useState(false);
  const [addDeductibleVisible, setAddDeductibleVisible] = useState(false);
  const [addSublimitVisible, setAddSublimitVisible] = useState(false);

  const includedPerils = new Set(
    specialtyPropertyInfo.report_json.policy_info?.included_perils ?? []
  );
  const isPerilDisabled = (peril: client.peril_type): boolean =>
    !includedPerils.has(peril);

  // --- Sublimit rows ---
  const extractedSublimits = selectedExtractedPolicy?.code
    ? (extractedSublimitInfo[selectedExtractedPolicy.code] ?? [])
    : [];

  const allSublimits = manualSublimits
    .map((limit) => ({ ...limit, manual: true }))
    .concat(
      extractedSublimits
        .map((limit) => ({ ...limit, manual: false }))
        .filter((limit) => !limit.is_removed)
    )
    .map((limit) => ({
      ...limit,
      classificationGroup: limit.classification ?? "",
      rowKey: `${limit.classification ?? ""}::${limit.sublimit_name}`,
    }))
    .sort((a, b) => {
      const byClassification =
        (classificationOrder[a.classificationGroup] ?? 99) -
        (classificationOrder[b.classificationGroup] ?? 99);
      if (byClassification !== 0) return byClassification;
      // Within a classification block, manual and extracted rows interleave by
      // their fractional sort_key (unsorted rows last).
      const ak = a.sort_key ?? null;
      const bk = b.sort_key ?? null;
      if (ak === null && bk === null) return 0;
      if (ak === null) return 1;
      if (bk === null) return -1;
      return ak < bk ? -1 : ak > bk ? 1 : 0;
    });

  type SublimitRow = (typeof allSublimits)[number];

  // Drag-reorder a sublimit within its classification block (manual + extracted
  // merged). A drop may re-key rows from either source list, so emit one update
  // per list.
  const onSublimitRowReorder = (
    e: DataTableRowReorderEvent<SublimitRow[]>
  ): void => {
    if (e.dragIndex === e.dropIndex) return; // dropped in place — no move
    const dropped = e.value;
    const changes = keysForDrop(
      dropped,
      e.dropIndex,
      (a, b) => a.classificationGroup === b.classificationGroup,
      (row) => row.sort_key
    );
    if (changes.length === 0) return;
    const entriesFor = (manual: boolean) =>
      changes
        .filter((change) => change.item.manual === manual)
        .map((change) => ({
          sublimit_name: change.item.sublimit_name,
          classification: change.item.classification ?? null,
          sort_key: change.sortKey,
        }));
    const manualEntries = entriesFor(true);
    const extractedEntries = entriesFor(false);
    const sublimitOrder: client.SublimitSortUpdate[] = [];
    if (manualEntries.length > 0) {
      sublimitOrder.push({
        group: "manual",
        filename: null,
        entries: manualEntries,
      });
    }
    if (extractedEntries.length > 0) {
      sublimitOrder.push({
        group: "extracted",
        filename: selectedExtractedPolicy?.code ?? null,
        entries: extractedEntries,
      });
    }
    if (sublimitOrder.length > 0) reorderMutation.mutate({ sublimitOrder });
  };

  // --- Deductible rows (derived from local state) ---
  const extractedDeductibles = deductibleInfo?.extracted_deductibles ?? {};
  const manualDeductibles = deductibleInfo?.manual_deductibles ?? {};
  const extractedDeductiblesForPolicy = selectedExtractedPolicy?.code
    ? (extractedDeductibles[selectedExtractedPolicy.code] ?? {})
    : {};
  const deductibleRows = buildDeductibleRows(
    extractedDeductiblesForPolicy,
    manualDeductibles
  );

  // --- Limit rows (derived from local state) ---
  const extractedLimitsAdvanced = limitInfo?.extracted_limits ?? {};
  const manualLimitsAdvanced = limitInfo?.manual_limits ?? {};
  const extractedLimitsForPolicy = selectedExtractedPolicy?.code
    ? (extractedLimitsAdvanced[selectedExtractedPolicy.code] ?? {})
    : {};
  const limitRows = buildLimitRows(
    extractedLimitsForPolicy,
    manualLimitsAdvanced
  );

  // Drag-reorder an excerpt row within its peril group. The peril resolves to
  // one entry (manual shadows extracted), so the new keys are written to that
  // entry's excerpt_sort_keys.
  const onDeductibleRowReorder = (
    e: DataTableRowReorderEvent<DeductibleRow[]>
  ): void => {
    if (e.dragIndex === e.dropIndex) return; // dropped in place — no move
    const dropped = e.value;
    const dragged = dropped[e.dropIndex];
    if (dragged === undefined) return;
    const changes = keysForDrop(
      dropped,
      e.dropIndex,
      (a, b) => a.perilType === b.perilType,
      (r) => r.sortKey
    );
    if (changes.length === 0) return;
    const isManual = manualDeductibles[dragged.perilType] != null;
    reorderMutation.mutate({
      deductibleOrder: [
        {
          group: isManual ? "manual" : "extracted",
          filename: isManual ? null : (selectedExtractedPolicy?.code ?? null),
          peril_type: dragged.perilType,
          excerpt_keys: Object.fromEntries(
            changes.map((change) => [
              change.item.deductibleName,
              change.sortKey,
            ])
          ),
        },
      ],
    });
  };

  const onLimitRowReorder = (e: DataTableRowReorderEvent<LimitRow[]>): void => {
    if (e.dragIndex === e.dropIndex) return; // dropped in place — no move
    const dropped = e.value;
    const dragged = dropped[e.dropIndex];
    if (dragged === undefined) return;
    const changes = keysForDrop(
      dropped,
      e.dropIndex,
      (a, b) => a.perilType === b.perilType,
      (r) => r.sortKey
    );
    if (changes.length === 0) return;
    const isManual = manualLimitsAdvanced[dragged.perilType] != null;
    reorderMutation.mutate({
      limitOrder: [
        {
          group: isManual ? "manual" : "extracted",
          filename: isManual ? null : (selectedExtractedPolicy?.code ?? null),
          peril_type: dragged.perilType,
          excerpt_keys: Object.fromEntries(
            changes.map((change) => [change.item.limitName, change.sortKey])
          ),
        },
      ],
    });
  };

  // --- Deductible edit handler: persists immediately (triggers regen) ---
  const handleDeductibleEdit = (data: DeductibleRow) => {
    let newValue = data.value;
    let newName = data.deductibleName;
    confirmDialog({
      message: (
        <div className={styles.verticalContainer}>
          <label>Name</label>
          <InputText
            className={styles.confirmDialogWidth}
            defaultValue={newName}
            onChange={(e) => {
              newName = e.target.value;
            }}
          />
          <label>Value</label>
          <InputTextarea
            className={styles.confirmDialogWidth}
            defaultValue={newValue}
            onChange={(e) => {
              newValue = e.target.value;
            }}
            autoResize
            rows={3}
          />
        </div>
      ),
      pt: { message: { style: { width: "500px" } } },
      style: { width: "600px" },
      header: `Edit ${data.deductibleName}`,
      acceptLabel: "Confirm",
      rejectLabel: "Cancel",
      accept: () => {
        updateReport.mutate({
          deductibleInfo: applyDeductibleEdit(deductibleInfo, {
            perilType: data.perilType,
            policyCode: selectedExtractedPolicy?.code,
            oldName: data.deductibleName,
            newName,
            newValue,
          }),
          rerunReport: true,
        });
      },
    });
  };

  // --- Deductible delete handler: persists immediately (triggers regen) ---
  const handleDeductibleDelete = (data: DeductibleRow) => {
    updateReport.mutate({
      deductibleInfo: applyDeductibleDelete(deductibleInfo, {
        perilType: data.perilType,
        policyCode: selectedExtractedPolicy?.code,
        name: data.deductibleName,
      }),
      rerunReport: true,
    });
  };

  // --- Limit edit handler: persists immediately (triggers regen) ---
  const handleLimitEdit = (data: LimitRow) => {
    let newValue = data.value;
    let newName = data.limitName;
    confirmDialog({
      message: (
        <div className={styles.verticalContainer}>
          <label>Name</label>
          <InputText
            className={styles.confirmDialogWidth}
            defaultValue={newName}
            onChange={(e) => {
              newName = e.target.value;
            }}
          />
          <label>Value</label>
          <InputTextarea
            className={styles.confirmDialogWidth}
            defaultValue={newValue}
            onChange={(e) => {
              newValue = e.target.value;
            }}
            autoResize
            rows={3}
          />
        </div>
      ),
      pt: { message: { style: { width: "500px" } } },
      style: { width: "600px" },
      header: `Edit ${data.limitName}`,
      acceptLabel: "Confirm",
      rejectLabel: "Cancel",
      accept: () => {
        updateReport.mutate({
          advancedLimitInfo: applyLimitEdit(limitInfo, {
            perilType: data.perilType,
            policyCode: selectedExtractedPolicy?.code,
            oldName: data.limitName,
            newName,
            newValue,
          }),
          rerunReport: true,
        });
      },
    });
  };

  // --- Limit delete handler: persists immediately (triggers regen) ---
  const handleLimitDelete = (data: LimitRow) => {
    updateReport.mutate({
      advancedLimitInfo: applyLimitDelete(limitInfo, {
        perilType: data.perilType,
        policyCode: selectedExtractedPolicy?.code,
        name: data.limitName,
      }),
      rerunReport: true,
    });
  };

  return (
    <Card title="Policy Fields">
      <div
        className={styles.horizontalContainer}
        style={{ marginBottom: "16px" }}
      >
        <Dropdown
          value={selectedExtractedPolicy}
          options={allPolicyValues}
          onChange={(e) => setSelectedExtractedPolicy(e.value)}
          optionLabel="label"
          style={{ maxWidth: "440px" }}
        />
        {policyFormMismatch && (
          <Message
            severity="warn"
            style={{ maxWidth: "560px" }}
            content={
              <span>
                Selected form does not match the saved policy form "
                {savedPolicyFormName}". You can update it on the{" "}
                <Link
                  to={ROUTES.DASHBOARD_REPORT(
                    specialtyPropertyInfo.report_id,
                    "general-input"
                  )}
                >
                  General tab
                </Link>
                .
              </span>
            }
          />
        )}
      </div>

      <TabView
        activeIndex={activeTabIndex}
        onTabChange={(e) => setActiveTabIndex(e.index)}
      >
        <TabPanel header="Limits">
          <div style={{ marginBottom: "12px" }}>
            <Button
              size="small"
              disabled={isMutating}
              icon="pi pi-plus"
              label="Add limit"
              onClick={() => setAddLimitVisible(true)}
            />
            <AddManualEntryDialog
              visible={addLimitVisible}
              header="Add Limit"
              namePlaceholder="Limit name"
              descriptionPlaceholder="Limit description"
              isDuplicateName={(peril, name) =>
                limitRows.some(
                  (row) =>
                    row.perilType === peril &&
                    row.limitName.toLowerCase() === name.toLowerCase()
                )
              }
              onHide={() => setAddLimitVisible(false)}
              onConfirm={(peril, limitName, limitValue) => {
                updateReport.mutate({
                  advancedLimitInfo: applyLimitAdd(limitInfo, {
                    perilType: peril,
                    policyCode: selectedExtractedPolicy?.code,
                    name: limitName,
                    value: limitValue,
                  }),
                  rerunReport: true,
                });
              }}
            />
          </div>
          <DataTable
            value={limitRows}
            className={styles.policyTable}
            scrollable
            scrollHeight={"70vh"}
            size="small"
            rowGroupMode="subheader"
            groupRowsBy="readablePerilName"
            rowGroupHeaderTemplate={(data: LimitRow) => (
              <strong
                className={
                  isPerilDisabled(data.perilType)
                    ? styles.disabledPerilGroupHeader
                    : undefined
                }
              >
                {data.readablePerilName}
              </strong>
            )}
            rowClassName={(data: LimitRow) =>
              isPerilDisabled(data.perilType) ? styles.disabledPerilRow : ""
            }
            reorderableRows={!isMutating}
            onRowReorder={onLimitRowReorder}
          >
            <Column rowReorder style={{ width: "3rem" }} />
            <Column header="Limit" field="limitName" />
            <Column header="Value" field="value" />
            <Column
              header="From"
              body={(data: LimitRow) => (
                <>
                  {data.manual ? (
                    <i className="pi pi-user" />
                  ) : (
                    <i className="pi pi-file" />
                  )}
                </>
              )}
            />
            <Column
              header=""
              body={(data: LimitRow) => (
                <div className={styles.horizontalContainer}>
                  <Button
                    icon="pi pi-pencil"
                    style={{ width: "28px", height: "28px" }}
                    text
                    disabled={isMutating}
                    onClick={() => handleLimitEdit(data)}
                  />
                  <Button
                    icon="pi pi-trash"
                    severity="danger"
                    style={{ width: "28px", height: "28px" }}
                    text
                    disabled={isMutating}
                    onClick={() => handleLimitDelete(data)}
                  />
                </div>
              )}
            />
          </DataTable>
        </TabPanel>
        <TabPanel header="Sublimits">
          <div style={{ marginBottom: "12px" }}>
            <Button
              size="small"
              disabled={isMutating}
              icon="pi pi-plus"
              label="Add sublimit"
              onClick={() => setAddSublimitVisible(true)}
            />
            <AddSublimitDialog
              visible={addSublimitVisible}
              onHide={() => setAddSublimitVisible(false)}
              isDuplicateName={(name, classification) =>
                // A duplicate is any existing row in the same classification
                // with the same name (which would also collide on the table
                // row key).
                allSublimits.some(
                  (limit) =>
                    (limit.classification ?? null) === classification &&
                    limit.sublimit_name === name
                )
              }
              onConfirm={(name, value, classification) => {
                updateReport.mutate({
                  sublimitInfo: {
                    extracted_sublimits: extractedSublimitInfo,
                    manual_sublimits: [
                      ...manualSublimits,
                      {
                        sublimit_name: name,
                        // Default sublimits carry a monetary value; the other
                        // categories describe a time/distance/period, so leave
                        // their text untouched.
                        sublimit_description:
                          classification === null
                            ? formatMonetaryValue(value)
                            : value.trim(),
                        document_excerpt: null,
                        classification,
                      },
                    ],
                  },
                });
              }}
            />
          </div>
          <DataTable
            value={allSublimits}
            className={styles.policyTable}
            scrollable
            scrollHeight={"70vh"}
            size="small"
            dataKey="rowKey"
            rowGroupMode="subheader"
            groupRowsBy="classificationGroup"
            rowGroupHeaderTemplate={(data) => {
              const label = readableClassification(data.classification);
              return label ? <strong>{label}</strong> : null;
            }}
            reorderableRows={!isMutating}
            onRowReorder={onSublimitRowReorder}
          >
            <Column rowReorder style={{ width: "3rem" }} />
            <Column header="Sublimit" field="sublimit_name" />
            <Column
              header="Value"
              body={(data) => data.manual_override ?? data.sublimit_description}
            />
            <Column
              header="Excerpt"
              body={(data) =>
                data.document_excerpt && (
                  <TooltipWrapper
                    tooltipText={data.document_excerpt}
                    tooltipProps={{ position: "left" }}
                  >
                    <i className="pi pi-info-circle" />
                  </TooltipWrapper>
                )
              }
            />
            <Column
              header="From"
              body={(data: client.Sublimit & { manual: boolean }) => (
                <>
                  {data.manual ||
                  (data.manual_override &&
                    data.manual_override !== data.sublimit_description) ? (
                    <i className="pi pi-user" />
                  ) : (
                    <i className="pi pi-file" />
                  )}
                </>
              )}
            />
            <Column
              header=""
              body={(data) => {
                const handleEdit = () => {
                  let newValue =
                    data.manual_override ?? data.sublimit_description;
                  let newName = data.sublimit_name;
                  confirmDialog({
                    message: (
                      <div className={styles.verticalContainer}>
                        <label>Name</label>
                        <InputText
                          className={styles.confirmDialogWidth}
                          defaultValue={newName}
                          onChange={(e) => {
                            newName = e.target.value;
                          }}
                        />
                        <label>Value</label>
                        <InputTextarea
                          className={styles.confirmDialogWidth}
                          defaultValue={newValue}
                          onChange={(e) => {
                            newValue = e.target.value;
                          }}
                          autoResize
                          rows={3}
                        />
                      </div>
                    ),
                    pt: { message: { style: { width: "500px" } } },
                    style: { width: "600px" },
                    header: "Edit Sublimit",
                    acceptLabel: "Confirm",
                    rejectLabel: "Cancel",
                    accept: async () => {
                      if (!data.manual && !selectedExtractedPolicy?.code)
                        return;
                      updateReport.mutate({
                        sublimitInfo: applySublimitEdit(
                          {
                            extracted_sublimits: extractedSublimitInfo,
                            manual_sublimits: manualSublimits,
                          },
                          {
                            manual: data.manual,
                            policyCode: selectedExtractedPolicy?.code,
                            oldName: data.sublimit_name,
                            classification: data.classification,
                            documentExcerpt: data.document_excerpt ?? null,
                            newName,
                            newValue,
                          }
                        ),
                      });
                    },
                  });
                };

                const handleDelete = () => {
                  if (!data.manual && !selectedExtractedPolicy?.code) return;
                  updateReport.mutate({
                    sublimitInfo: applySublimitDelete(
                      {
                        extracted_sublimits: extractedSublimitInfo,
                        manual_sublimits: manualSublimits,
                      },
                      {
                        manual: data.manual,
                        policyCode: selectedExtractedPolicy?.code,
                        name: data.sublimit_name,
                        description: data.sublimit_description,
                        classification: data.classification,
                      }
                    ),
                  });
                };

                return (
                  <div className={styles.horizontalContainer}>
                    <Button
                      icon="pi pi-pencil"
                      style={{ width: "28px", height: "28px" }}
                      text
                      disabled={isMutating}
                      onClick={handleEdit}
                    />
                    <Button
                      icon="pi pi-trash"
                      severity="danger"
                      style={{ width: "28px", height: "28px" }}
                      text
                      disabled={isMutating}
                      onClick={handleDelete}
                    />
                  </div>
                );
              }}
            />
          </DataTable>
        </TabPanel>
        <TabPanel header="Deductibles">
          <div style={{ marginBottom: "12px" }}>
            <Button
              size="small"
              disabled={isMutating}
              icon="pi pi-plus"
              label="Add deductible"
              onClick={() => setAddDeductibleVisible(true)}
            />
            <AddManualEntryDialog
              visible={addDeductibleVisible}
              header="Add Deductible"
              namePlaceholder="Deductible name"
              descriptionPlaceholder="Deductible description"
              isDuplicateName={(peril, name) =>
                deductibleRows.some(
                  (row) =>
                    row.perilType === peril &&
                    row.deductibleName.toLowerCase() === name.toLowerCase()
                )
              }
              onHide={() => setAddDeductibleVisible(false)}
              onConfirm={(peril, deductibleName, deductibleValue) => {
                updateReport.mutate({
                  deductibleInfo: applyDeductibleAdd(deductibleInfo, {
                    perilType: peril,
                    policyCode: selectedExtractedPolicy?.code,
                    name: deductibleName,
                    value: deductibleValue,
                  }),
                  rerunReport: true,
                });
              }}
            />
          </div>
          <DataTable
            value={deductibleRows}
            className={styles.policyTable}
            scrollable
            scrollHeight={"70vh"}
            size="small"
            rowGroupMode="subheader"
            groupRowsBy="readablePerilName"
            rowGroupHeaderTemplate={(data: DeductibleRow) => (
              <strong
                className={
                  isPerilDisabled(data.perilType)
                    ? styles.disabledPerilGroupHeader
                    : undefined
                }
              >
                {data.readablePerilName}
              </strong>
            )}
            rowClassName={(data: DeductibleRow) =>
              isPerilDisabled(data.perilType) ? styles.disabledPerilRow : ""
            }
            reorderableRows={!isMutating}
            onRowReorder={onDeductibleRowReorder}
          >
            <Column rowReorder style={{ width: "3rem" }} />
            <Column header="Deductible" field="deductibleName" />
            <Column header="Value" field="value" />
            <Column
              header="From"
              body={(data: DeductibleRow) => (
                <>
                  {data.manual ? (
                    <i className="pi pi-user" />
                  ) : (
                    <i className="pi pi-file" />
                  )}
                </>
              )}
            />
            <Column
              header=""
              body={(data: DeductibleRow) => (
                <div className={styles.horizontalContainer}>
                  <Button
                    icon="pi pi-pencil"
                    style={{ width: "28px", height: "28px" }}
                    text
                    disabled={isMutating}
                    onClick={() => handleDeductibleEdit(data)}
                  />
                  <Button
                    icon="pi pi-trash"
                    severity="danger"
                    style={{ width: "28px", height: "28px" }}
                    text
                    disabled={isMutating}
                    onClick={() => handleDeductibleDelete(data)}
                  />
                </div>
              )}
            />
          </DataTable>
        </TabPanel>
      </TabView>
    </Card>
  );
};
